import type { ReactNode } from 'react';
import { memo } from 'react';
import type { TFunction } from 'i18next';

interface CropToolbarProps {
  t: TFunction;
  canUndo: boolean;
  canRedo: boolean;
  isSaving: boolean;
  canRestoreOriginal: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onDownload: () => void;
  onRestoreOriginal: () => void;
  onCancel: () => void;
  onSave: () => void;
}

export const CropToolbar = memo(function CropToolbar({
  t,
  canRedo,
  canUndo,
  canRestoreOriginal,
  isSaving,
  onCancel,
  onDownload,
  onRedo,
  onRestoreOriginal,
  onSave,
  onUndo
}: CropToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToolbarIconButton ariaLabel={t('history.crop.toolbar.undo')} onClick={onUndo} disabled={!canUndo}>
        <UndoIcon />
      </ToolbarIconButton>
      <ToolbarIconButton ariaLabel={t('history.crop.toolbar.redo')} onClick={onRedo} disabled={!canRedo}>
        <RedoIcon />
      </ToolbarIconButton>
      <ToolbarIconButton ariaLabel={t('history.crop.toolbar.download')} onClick={onDownload}>
        <DownloadIcon />
      </ToolbarIconButton>
      <ToolbarIconButton
        ariaLabel={t('history.crop.toolbar.restore')}
        onClick={onRestoreOriginal}
        disabled={!canRestoreOriginal}
      >
        <RestoreIcon />
      </ToolbarIconButton>
      <ToolbarButton label={t('history.crop.toolbar.cancel')} onClick={onCancel} />
      <button
        type="button"
        className="rounded-full bg-ai-primary px-4 py-2 text-sm font-semibold text-white shadow-ai-card transition hover:bg-ai-primary/90 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:cursor-not-allowed disabled:bg-ai-text-disabled"
        onClick={onSave}
        disabled={isSaving}
      >
        {isSaving ? t('history.crop.toolbar.saving') : t('history.crop.toolbar.save')}
      </button>
    </div>
  );
});

interface ToolbarButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function ToolbarButton({ label, onClick, disabled }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className="rounded-full border border-ai-surface-glow/30 px-3 py-1 text-sm font-semibold text-ai-text-secondary transition hover:border-ai-primary/40 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:cursor-not-allowed disabled:text-ai-text-disabled"
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

interface ToolbarIconButtonProps {
  ariaLabel: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}

function ToolbarIconButton({ ariaLabel, onClick, disabled, children }: ToolbarIconButtonProps) {
  return (
    <button
      type="button"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-ai-surface-glow/30 text-ai-text-secondary transition hover:border-ai-primary/40 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:cursor-not-allowed disabled:text-ai-text-disabled"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {children}
      <span className="sr-only">{ariaLabel}</span>
    </button>
  );
}

function UndoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5">
      <path
        d="M7 9H5.5a4.5 4.5 0 000 9H13a4 4 0 000-8h-2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 5L4 9l4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RedoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5">
      <path
        d="M17 9h1.5a4.5 4.5 0 010 9H11a4 4 0 010-8h2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 5l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5">
      <path
        d="M12 4v11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 12l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 20h14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RestoreIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5">
      <path
        d="M20 6v6h-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 12a8 8 0 10-2.344 5.657L20 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
